import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminHealthApi } from '../lib/api';
import { useAuthStore } from '../store/authStore';
import { Navigate } from 'react-router-dom';
import {
  CpuChipIcon,
  BeakerIcon,
  ShieldCheckIcon,
  PlayIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

type Tab = 'providers' | 'validation' | 'bias';
type Period = '24h' | '7d' | '30d';

export default function AdminSystemHealthPage() {
  const { user } = useAuthStore();
  const [tab, setTab] = useState<Tab>('providers');
  const [period, setPeriod] = useState<Period>('24h');

  if (!user?.isSuperAdmin) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-900">System Health</h1>
          <p className="text-sm text-gray-500 mt-1">AI provider metrics, model validation, and bias testing</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {([
          { id: 'providers' as Tab, label: 'Providers', icon: CpuChipIcon },
          { id: 'validation' as Tab, label: 'Model Validation', icon: BeakerIcon },
          { id: 'bias' as Tab, label: 'Bias Testing', icon: ShieldCheckIcon },
        ]).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm rounded-md transition-colors ${
              tab === t.id ? 'bg-white text-navy-900 shadow-sm font-medium' : 'text-gray-600 hover:text-navy-900'
            }`}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'providers' && <ProvidersTab period={period} setPeriod={setPeriod} />}
      {tab === 'validation' && <ValidationTab />}
      {tab === 'bias' && <BiasTestingTab />}
    </div>
  );
}

// ─── Providers Tab ───────────────────────────────────────────────────────────

function ProvidersTab({ period, setPeriod }: { period: Period; setPeriod: (p: Period) => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-system-health', period],
    queryFn: () => adminHealthApi.getSystemHealth(period).then((r) => r.data),
    refetchInterval: 30000,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        {(['24h', '7d', '30d'] as Period[]).map((p) => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            className={`px-3 py-1.5 text-sm rounded-md ${
              period === p ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {p}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-gray-100 rounded-lg" />
          ))}
        </div>
      ) : (
        <>
          {/* Circuit Breaker Status */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="font-semibold text-navy-900 mb-4">Circuit Breaker Status</h3>
            <div className="grid grid-cols-3 gap-4">
              {Object.entries(data?.circuitBreaker || {}).map(([name, health]: [string, any]) => (
                <div key={name} className={`p-4 rounded-lg border ${
                  health.status === 'healthy' ? 'border-green-200 bg-green-50' :
                  health.status === 'degraded' ? 'border-amber-200 bg-amber-50' :
                  'border-red-200 bg-red-50'
                }`}>
                  <p className="font-medium capitalize">{name}</p>
                  <p className="text-sm mt-1">
                    Status: <span className="font-semibold capitalize">{health.status}</span>
                  </p>
                  <p className="text-xs text-gray-500">Consecutive failures: {health.consecutiveFailures}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Provider Metrics */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="font-semibold text-navy-900 mb-4">Provider Performance</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-2 font-medium text-gray-500">Provider</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-500">Calls</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-500">Success</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-500">Error</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-500">P50</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-500">P95</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-500">P99</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-500">Fallback</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.providers?.providers || []).map((p: any) => (
                    <tr key={p.name} className="border-b border-gray-100">
                      <td className="py-3 px-2 font-medium capitalize">{p.name}</td>
                      <td className="text-right py-3 px-2">{p.callCount.toLocaleString()}</td>
                      <td className="text-right py-3 px-2 text-green-600">{p.successRate}%</td>
                      <td className="text-right py-3 px-2 text-red-600">{p.errorRate}%</td>
                      <td className="text-right py-3 px-2">{p.latencyP50}ms</td>
                      <td className="text-right py-3 px-2">{p.latencyP95}ms</td>
                      <td className="text-right py-3 px-2">{p.latencyP99}ms</td>
                      <td className="text-right py-3 px-2">{p.fallbackRate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data?.providers?.totalCalls > 0 && (
              <div className="mt-4 flex gap-6 text-xs text-gray-500">
                <span>Total: {data.providers.totalCalls.toLocaleString()} calls</span>
                <span>Overall Success: {data.providers.overallSuccessRate}%</span>
                <span>Overall Fallback: {data.providers.overallFallbackRate}%</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Validation Tab ──────────────────────────────────────────────────────────

function ValidationTab() {
  const queryClient = useQueryClient();

  const { data: runs, isLoading } = useQuery({
    queryKey: ['validation-runs'],
    queryFn: () => adminHealthApi.getValidationRuns().then((r) => r.data),
  });

  const triggerMutation = useMutation({
    mutationFn: () => adminHealthApi.triggerValidation().then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validation-runs'] });
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Validates AI model accuracy against curated test datasets. Measures precision, recall, and F1 score per use case.
        </p>
        <button
          onClick={() => triggerMutation.mutate()}
          disabled={triggerMutation.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm rounded-md hover:bg-teal-700 disabled:opacity-50"
        >
          <PlayIcon className="h-4 w-4" />
          {triggerMutation.isPending ? 'Starting...' : 'Run Validation'}
        </button>
      </div>

      {isLoading ? (
        <div className="animate-pulse h-48 bg-gray-100 rounded-lg" />
      ) : runs && runs.length > 0 ? (
        <div className="space-y-4">
          {runs.map((run: any) => (
            <ValidationRunCard key={run.id} run={run} />
          ))}
        </div>
      ) : (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <BeakerIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No validation runs yet. Click "Run Validation" to start.</p>
        </div>
      )}

      <ReferenceBox title="Model Risk Guidance Alignment (SR 26-2 / OCC 2026-13)" items={[
        'Model validation tests AI output accuracy against curated test datasets',
        'Precision, recall, and F1 scores measured per use case',
        'Independent from model development (test fixtures maintained separately)',
        'Results stored with timestamps for audit trail',
      ]} />
    </div>
  );
}

function ValidationRunCard({ run }: { run: any }) {
  const results = Array.isArray(run.results) ? run.results : [];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <StatusBadge status={run.status} />
          <span className="text-sm text-gray-500">
            {new Date(run.runDate).toLocaleString()}
          </span>
          {run.duration && <span className="text-xs text-gray-400">{run.duration}s</span>}
        </div>
        {run.overallAccuracy != null && (
          <span className="text-lg font-bold text-navy-900">{run.overallAccuracy}% accuracy</span>
        )}
      </div>

      {results.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-2 font-medium text-gray-500">Use Case</th>
                <th className="text-right py-2 px-2 font-medium text-gray-500">Tests</th>
                <th className="text-right py-2 px-2 font-medium text-gray-500">Passed</th>
                <th className="text-right py-2 px-2 font-medium text-gray-500">Precision</th>
                <th className="text-right py-2 px-2 font-medium text-gray-500">Recall</th>
                <th className="text-right py-2 px-2 font-medium text-gray-500">F1</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r: any) => (
                <tr key={r.useCase} className="border-b border-gray-100">
                  <td className="py-2 px-2">{r.useCase}</td>
                  <td className="text-right py-2 px-2">{r.totalTests}</td>
                  <td className="text-right py-2 px-2 text-green-600">{r.passed}</td>
                  <td className="text-right py-2 px-2">{r.precision != null ? `${r.precision}%` : '—'}</td>
                  <td className="text-right py-2 px-2">{r.recall != null ? `${r.recall}%` : '—'}</td>
                  <td className="text-right py-2 px-2 font-medium">{r.f1 != null ? `${r.f1}%` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Bias Testing Tab ────────────────────────────────────────────────────────

function BiasTestingTab() {
  const queryClient = useQueryClient();

  const { data: runs, isLoading } = useQuery({
    queryKey: ['bias-test-runs'],
    queryFn: () => adminHealthApi.getBiasTestRuns().then((r) => r.data),
  });

  const triggerMutation = useMutation({
    mutationFn: () => adminHealthApi.triggerBiasTesting().then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bias-test-runs'] });
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Tests AI outputs for differential treatment across protected class dimensions using paired content analysis.
        </p>
        <button
          onClick={() => triggerMutation.mutate()}
          disabled={triggerMutation.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm rounded-md hover:bg-teal-700 disabled:opacity-50"
        >
          <PlayIcon className="h-4 w-4" />
          {triggerMutation.isPending ? 'Starting...' : 'Run Bias Tests'}
        </button>
      </div>

      {isLoading ? (
        <div className="animate-pulse h-48 bg-gray-100 rounded-lg" />
      ) : runs && runs.length > 0 ? (
        <div className="space-y-4">
          {runs.map((run: any) => (
            <BiasTestRunCard key={run.id} run={run} />
          ))}
        </div>
      ) : (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <ShieldCheckIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No bias tests run yet. Click "Run Bias Tests" to start.</p>
        </div>
      )}

      <ReferenceBox title="Industry Guidance" items={[
        'NIST AI 100-1: Test for disparate impact across demographic groups',
        'CFPB Supervisory Highlights: Fair lending exam expectations for automated tools',
        'SR 26-2 / OCC 2026-13: Model validation, back-testing, sensitivity analysis (superseded SR 11-7, April 2026)',
        'FS AI RMF MEASURE MS-2.1–2.6: Bias detection and mitigation controls',
        'Threshold: p < 0.05 AND differential > 10% (Fisher\'s exact test)',
      ]} />
    </div>
  );
}

function BiasTestRunCard({ run }: { run: any }) {
  const results = Array.isArray(run.results) ? run.results : [];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <StatusBadge status={run.status} />
          {run.overallPass != null && (
            <span className={`flex items-center gap-1 text-sm font-medium ${run.overallPass ? 'text-green-700' : 'text-red-700'}`}>
              {run.overallPass ? <CheckCircleIcon className="h-4 w-4" /> : <XCircleIcon className="h-4 w-4" />}
              {run.overallPass ? 'All Dimensions Pass' : 'Bias Detected'}
            </span>
          )}
          <span className="text-sm text-gray-500">{new Date(run.runDate).toLocaleString()}</span>
          {run.duration && <span className="text-xs text-gray-400">{run.duration}s</span>}
        </div>
      </div>

      {run.flaggedDimensions?.length > 0 && (
        <div className="mb-4 flex gap-2">
          {run.flaggedDimensions.map((d: string) => (
            <span key={d} className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">{d}</span>
          ))}
        </div>
      )}

      {results.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-2 font-medium text-gray-500">Dimension</th>
                <th className="text-right py-2 px-2 font-medium text-gray-500">Tests</th>
                <th className="text-right py-2 px-2 font-medium text-gray-500">Baseline Rate</th>
                <th className="text-right py-2 px-2 font-medium text-gray-500">Variant Rate</th>
                <th className="text-right py-2 px-2 font-medium text-gray-500">Difference</th>
                <th className="text-right py-2 px-2 font-medium text-gray-500">p-Value</th>
                <th className="text-center py-2 px-2 font-medium text-gray-500">Result</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r: any) => (
                <tr key={r.dimension} className="border-b border-gray-100">
                  <td className="py-2 px-2">{r.dimension}</td>
                  <td className="text-right py-2 px-2">{r.testCount}</td>
                  <td className="text-right py-2 px-2">{(r.baselineRate * 100).toFixed(1)}%</td>
                  <td className="text-right py-2 px-2">{(r.variantRate * 100).toFixed(1)}%</td>
                  <td className="text-right py-2 px-2">{(Math.abs(r.difference) * 100).toFixed(1)}%</td>
                  <td className="text-right py-2 px-2">{r.pValue?.toFixed(4)}</td>
                  <td className="text-center py-2 px-2">
                    {r.significant ? (
                      <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">Flagged</span>
                    ) : (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Pass</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Shared Components ───────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string; icon: typeof CheckCircleIcon }> = {
    running: { bg: 'bg-blue-100', text: 'text-blue-700', icon: ClockIcon },
    completed: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircleIcon },
    failed: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircleIcon },
  };
  const c = config[status] || config.running;
  return (
    <span className={`flex items-center gap-1 px-2 py-0.5 ${c.bg} ${c.text} text-xs rounded-full font-medium`}>
      <c.icon className="h-3 w-3" />
      {status}
    </span>
  );
}

function ReferenceBox({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <p className="text-sm font-medium text-blue-900 mb-2">{title}</p>
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li key={i} className="text-xs text-blue-700 flex items-start gap-2">
            <span className="text-blue-400 mt-0.5">-</span>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}
